/**
 * Shared intake form for contact / bug / feature (CUSTOMER_FEEDBACK_PLAN Task 2.1).
 *
 * One component, `type` prop — divergent one-off forms are a finding.
 * localStorage: feedbackQueue:v1 (via web/src/lib/feedback/client.ts)
 */

import { useEffect, useId, useRef, useState, type FormEvent } from "react";
import { fieldCls, labelCls } from "../AuthShell.js";
import { Button } from "../ui/Button.js";
import { getCaptchaToken, isTurnstileConfigured } from "../../lib/captcha.js";
import {
  getElapsedMs,
  submitIncident,
  type Severity,
} from "../../lib/feedback/client.js";
import {
  buildFeedbackContext,
  CONTEXT_DISCLOSURE_FIELDS,
} from "../../lib/feedback/context.js";
import {
  INCIDENT_CATEGORIES,
  type IncidentCategoryId,
  type SurfaceId,
} from "../../lib/feedback/surfaces.js";

const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const MAX_NAME = 100;
const MAX_EMAIL = 200;
const MAX_BODY = 5000;
const COUNTER_AT = Math.floor(MAX_BODY * 0.8);

export type FeedbackFormType = "bug" | "feature" | "contact";

type FormState = "idle" | "submitting" | "success" | "error";

interface FormErrors {
  name?: string;
  email?: string;
  body?: string;
}

const SURFACE_FOR_TYPE: Record<FeedbackFormType, SurfaceId> = {
  contact: "form.contact",
  bug: "form.bug-report",
  feature: "form.feature-request",
};

const BODY_LABEL: Record<FeedbackFormType, string> = {
  contact: "Message",
  bug: "What happened",
  feature: "What would you like",
};

const SUBMIT_LABEL: Record<FeedbackFormType, string> = {
  contact: "Send message",
  bug: "Send report",
  feature: "Send request",
};

const SEVERITIES: { id: Severity; label: string }[] = [
  { id: "low", label: "Low" },
  { id: "normal", label: "Normal" },
  { id: "high", label: "High" },
];

export interface FeedbackFormProps {
  type: FeedbackFormType;
  /** Pre-fill the body (e.g. 404 "report this broken link"). */
  prefillBody?: string;
  /** Override surface id (e.g. page.404). */
  surfaceId?: SurfaceId;
  /** Override route recorded in context (pathname; will be redacted). */
  route?: string;
  /** Extra allowlisted payload merged into context when technical context is included. */
  payload?: Record<string, unknown>;
  /** Called once, after a successful (including queued) submission, with the ticket ref. */
  onSubmitted?: (ref: string) => void;
  className?: string;
}

export function FeedbackForm({
  type,
  prefillBody = "",
  surfaceId,
  route,
  payload,
  onSubmitted,
  className,
}: FeedbackFormProps) {
  const formId = useId();
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [body, setBody] = useState(prefillBody);
  const [category, setCategory] = useState<IncidentCategoryId | null>(null);
  const [severity, setSeverity] = useState<Severity>("normal");
  const [honeypot, setHoneypot] = useState("");
  const [includeContext, setIncludeContext] = useState(true);
  const [formState, setFormState] = useState<FormState>("idle");
  const [errors, setErrors] = useState<FormErrors>({});
  const [serverError, setServerError] = useState("");
  const [ticketRef, setTicketRef] = useState("");
  const [statusUrl, setStatusUrl] = useState("");
  const [queued, setQueued] = useState(false);
  const [copied, setCopied] = useState(false);
  const openedAt = useRef(Date.now());
  const emailGiven = useRef(false);

  useEffect(() => {
    openedAt.current = Date.now();
  }, [type]);

  useEffect(() => {
    if (prefillBody) setBody(prefillBody);
  }, [prefillBody]);

  function validate(): FormErrors {
    const errs: FormErrors = {};

    if (type === "contact" && name.length > MAX_NAME) {
      errs.name = `Name must be ${MAX_NAME} characters or fewer.`;
    }

    if (email.trim()) {
      if (email.length > MAX_EMAIL) {
        errs.email = `Email must be ${MAX_EMAIL} characters or fewer.`;
      } else if (!EMAIL_REGEX.test(email.trim())) {
        errs.email = "Enter a valid email address.";
      }
    }

    if (!body.trim()) {
      errs.body =
        type === "feature"
          ? "Please describe what you would like."
          : type === "bug"
            ? "Please describe what happened."
            : "Message is required.";
    } else if (body.length > MAX_BODY) {
      errs.body = `Must be ${MAX_BODY} characters or fewer.`;
    }

    return errs;
  }

  async function handleSubmit(e: FormEvent) {
    e.preventDefault();

    const errs = validate();
    if (Object.keys(errs).length > 0) {
      setErrors(errs);
      return;
    }

    setErrors({});
    setFormState("submitting");
    setServerError("");
    emailGiven.current = Boolean(email.trim());

    try {
      let captchaToken: string | undefined;
      try {
        captchaToken = await getCaptchaToken(type);
      } catch {
        if (isTurnstileConfigured()) {
          setServerError("Captcha check failed. Please try again.");
          setFormState("error");
          return;
        }
      }

      const resolvedSurface = surfaceId ?? SURFACE_FOR_TYPE[type];
      const context = buildFeedbackContext({
        surfaceId: resolvedSurface,
        route,
        payload: includeContext ? payload : { contextOmitted: true },
      });
      if (!includeContext) {
        // Scrub every field CONTEXT_DISCLOSURE_FIELDS lists as "included" so the
        // checkbox does what its label says. deviceClass/prefersColorScheme are
        // left as-is: their type is a low-cardinality literal union with no
        // "omitted" value, and neither is identifying on its own.
        context.browser = "omitted";
        context.os = "omitted";
        context.viewport = "0x0";
        context.timezone = "omitted";
        context.locale = "omitted";
        delete context.sessionRef;
        delete context.payload;
      }

      const result = await submitIncident({
        appId: context.appId,
        type,
        body: body.trim(),
        ...(type === "contact" && name.trim() ? { contactName: name.trim() } : {}),
        ...(email.trim() ? { contactEmail: email.trim() } : {}),
        ...(type === "bug" && category ? { category } : {}),
        ...(type === "bug" ? { severity } : {}),
        context,
        hp: honeypot,
        elapsedMs: getElapsedMs(openedAt.current),
        ...(captchaToken ? { captchaToken } : {}),
      });

      if (result.ok) {
        setTicketRef(result.ref);
        setStatusUrl(result.statusUrl);
        setQueued(result.queued);
        setFormState("success");
        onSubmitted?.(result.ref);
      } else {
        setServerError(result.error);
        setFormState("error");
      }
    } catch {
      // submitIncident queues on network failure; this is a last-resort guard.
      setServerError("Something went wrong. Your message was kept — please try again.");
      setFormState("error");
    }
  }

  async function copyRef() {
    try {
      await navigator.clipboard.writeText(ticketRef);
      setCopied(true);
      window.setTimeout(() => setCopied(false), 2000);
    } catch {
      // ignore
    }
  }

  if (formState === "success") {
    return (
      <div
        role="status"
        className={`rounded-xl border border-success/20 bg-success/5 p-5 text-ink ${className ?? ""}`}
      >
        <p className="font-medium">
          {queued
            ? "Got it — we'll send this automatically when you're back online."
            : `Got it. Your reference is ${ticketRef}.`}
        </p>
        {!queued && ticketRef && ticketRef !== "QUEUED" ? (
          <div className="mt-3 flex flex-wrap items-center gap-2">
            <code className="rounded-lg bg-white px-2 py-1 font-mono text-sm text-ink ring-1 ring-line">
              {ticketRef}
            </code>
            <Button type="button" variant="secondary" size="sm" onClick={() => void copyRef()}>
              {copied ? "Copied" : "Copy"}
            </Button>
          </div>
        ) : null}
        {statusUrl ? (
          <p className="mt-3 text-sm">
            <a href={statusUrl} className="font-medium text-brand-700 underline-offset-2 hover:underline">
              Check status
            </a>
          </p>
        ) : null}
        {!emailGiven.current && statusUrl ? (
          <p className="mt-2 text-sm font-medium">
            Save this link — it&apos;s the only way to check status.
          </p>
        ) : null}
        <p className="mt-3 text-sm text-muted">
          We read everything. You may not get a personal reply, but every message is reviewed.
        </p>
      </div>
    );
  }

  const isSubmitting = formState === "submitting";
  const nearCap = body.length >= COUNTER_AT;
  const bodyId = `${formId}-body`;
  const emailId = `${formId}-email`;
  const nameId = `${formId}-name`;
  const hpId = `${formId}-website`;

  return (
    <form
      onSubmit={(e) => void handleSubmit(e)}
      noValidate
      className={`space-y-5 ${className ?? ""}`}
    >
      <div className="sr-only" aria-hidden="true">
        <label htmlFor={hpId}>Leave this field blank</label>
        <input
          id={hpId}
          name="website"
          type="text"
          tabIndex={-1}
          autoComplete="off"
          value={honeypot}
          onChange={(e) => setHoneypot(e.target.value)}
        />
      </div>

      {type === "contact" ? (
        <div>
          <label htmlFor={nameId} className={labelCls}>
            Name <span className="font-normal text-muted">(optional)</span>
          </label>
          <input
            id={nameId}
            name="name"
            type="text"
            className={`${fieldCls}${errors.name ? " border-danger" : ""}`}
            value={name}
            maxLength={MAX_NAME}
            autoComplete="name"
            aria-invalid={Boolean(errors.name)}
            aria-describedby={errors.name ? `${nameId}-error` : undefined}
            onChange={(e) => setName(e.target.value)}
          />
          {errors.name ? (
            <p id={`${nameId}-error`} role="alert" className="mt-1 text-xs text-danger">
              {errors.name}
            </p>
          ) : null}
        </div>
      ) : null}

      {type === "bug" ? (
        <fieldset>
          <legend className={labelCls}>Category <span className="font-normal text-muted">(optional)</span></legend>
          <div className="mt-2 flex flex-wrap gap-2">
            {INCIDENT_CATEGORIES.map((c) => {
              const selected = category === c.id;
              return (
                <button
                  key={c.id}
                  type="button"
                  aria-pressed={selected}
                  className={
                    "rounded-lg border px-3 py-1.5 text-sm transition " +
                    (selected
                      ? "border-brand-600 bg-brand-50 font-medium text-brand-700"
                      : "border-line bg-white text-ink hover:bg-slate-50") +
                    " focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-500/40"
                  }
                  onClick={() => setCategory(selected ? null : c.id)}
                >
                  {c.label}
                </button>
              );
            })}
          </div>
        </fieldset>
      ) : null}

      {type === "bug" ? (
        <fieldset>
          <legend className={labelCls}>
            Severity <span className="font-normal text-muted">(optional)</span>
          </legend>
          <div className="mt-2 flex flex-wrap gap-2">
            {SEVERITIES.map((s) => {
              const selected = severity === s.id;
              return (
                <button
                  key={s.id}
                  type="button"
                  aria-pressed={selected}
                  className={
                    "rounded-lg border px-3 py-1.5 text-sm transition " +
                    (selected
                      ? "border-brand-600 bg-brand-50 font-medium text-brand-700"
                      : "border-line bg-white text-ink hover:bg-slate-50") +
                    " focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-500/40"
                  }
                  onClick={() => setSeverity(s.id)}
                >
                  {s.label}
                </button>
              );
            })}
          </div>
        </fieldset>
      ) : null}

      <div>
        <label htmlFor={bodyId} className={labelCls}>
          {BODY_LABEL[type]}
        </label>
        <textarea
          id={bodyId}
          name="message"
          className={`${fieldCls} min-h-[8rem] resize-y${errors.body ? " border-danger" : ""}`}
          rows={6}
          maxLength={MAX_BODY}
          value={body}
          aria-invalid={Boolean(errors.body)}
          aria-describedby={errors.body ? `${bodyId}-error` : undefined}
          onChange={(e) => setBody(e.target.value)}
        />
        {nearCap ? (
          <p className="mt-1 text-xs text-muted">
            {body.length} / {MAX_BODY}
          </p>
        ) : null}
        {errors.body ? (
          <p id={`${bodyId}-error`} role="alert" className="mt-1 text-xs text-danger">
            {errors.body}
          </p>
        ) : null}
      </div>

      <div>
        <label htmlFor={emailId} className={labelCls}>
          Email (optional) — only if you&apos;d like a reply
        </label>
        <input
          id={emailId}
          name="email"
          type="email"
          inputMode="email"
          autoComplete="email"
          className={`${fieldCls}${errors.email ? " border-danger" : ""}`}
          value={email}
          maxLength={MAX_EMAIL}
          aria-invalid={Boolean(errors.email)}
          aria-describedby={errors.email ? `${emailId}-error` : undefined}
          onChange={(e) => setEmail(e.target.value)}
        />
        {errors.email ? (
          <p id={`${emailId}-error`} role="alert" className="mt-1 text-xs text-danger">
            {errors.email}
          </p>
        ) : null}
      </div>

      <details className="text-sm text-muted">
        <summary className="cursor-pointer text-ink hover:text-brand-700">
          What&apos;s included with this report
        </summary>
        <ul className="mt-2 list-disc space-y-1 pl-5">
          {CONTEXT_DISCLOSURE_FIELDS.map((f) => (
            <li key={f.key}>{f.label}</li>
          ))}
        </ul>
        <label className="mt-3 flex items-start gap-2 text-ink">
          <input
            type="checkbox"
            checked={!includeContext}
            onChange={(e) => setIncludeContext(!e.target.checked)}
            className="mt-1"
          />
          <span>Omit technical context from this report</span>
        </label>
      </details>

      {formState === "error" && serverError ? (
        <p role="alert" className="text-sm text-danger">
          {serverError}
        </p>
      ) : null}

      <Button type="submit" disabled={isSubmitting}>
        {isSubmitting ? "Sending…" : SUBMIT_LABEL[type]}
      </Button>
    </form>
  );
}
